import * as React from 'react'
import { To, useLocation, useNavigate } from 'react-router-dom'

import { useDialogState } from '../../components'
import { SpecificChartFunctionality, TIMEFRAME, TimeframeAndIntradayType } from '../../constants/common'
import Chart from '../../models/chart'
import { useChartsPathLocation } from './use-charts-path-location'
import { generateDateRangeGroups, generatePathForTimeframeDateRange, generateTimeframeGroups } from './utils'

export function useTimeframeDateRangeGroups({
  chartModel,
  isPremium,
}: {
  chartModel: Chart | null
  isPremium: boolean
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const customDateRangeDialogState = useDialogState()
  const chartLayoutModel = React.useMemo(() => chartModel?.chart_layout() ?? null, [chartModel])
  const chartIndex = chartLayoutModel?.getAllCharts().findIndex((chart) => chartModel!.eql(chart)) ?? -1
  const { getChartsToPath } = useChartsPathLocation({ chartLayoutModel, chartIndex })
  const specificChartFunctionality = chartLayoutModel?.specificChartFunctionality ?? SpecificChartFunctionality.default

  const generateChartLayoutRouterPath = React.useCallback(
    (props: { timeframe: TimeframeAndIntradayType; dateRange?: string | null; ticker?: string }) => {
      if (specificChartFunctionality === SpecificChartFunctionality.chartPage) {
        return getChartsToPath({
          ticker: props.ticker,
          timeframe: props.timeframe as TIMEFRAME,
          dateRange: props.dateRange,
        })
      }

      return generatePathForTimeframeDateRange({
        isPremium,
        pathname: location.pathname,
        timeframe: props.timeframe,
        dateRange: props.dateRange,
        specificChartFunctionality,
      })
    },
    [getChartsToPath, isPremium, specificChartFunctionality, location.pathname]
  )

  const timeFrameGroups = React.useMemo(
    () =>
      generateTimeframeGroups(isPremium, (timeFrame) =>
        generateChartLayoutRouterPath({ timeframe: timeFrame.value, dateRange: null })
      ),
    [isPremium, generateChartLayoutRouterPath]
  )

  const dateRangeGroups = React.useMemo(
    () =>
      generateDateRangeGroups(isPremium, chartModel?.dateRange, (dateRange) =>
        generateChartLayoutRouterPath({ timeframe: dateRange.timeframe!, dateRange: dateRange.value })
      ),
    [generateChartLayoutRouterPath, chartModel?.dateRange, isPremium]
  )

  const handleDateRangeSelect = React.useCallback(
    ({ value, timeframe, href }: { value: string; timeframe: TIMEFRAME; href?: To }) => {
      if (!timeframe) {
        if (isPremium) {
          customDateRangeDialogState.show()
        }
        return
      }

      if (!href) {
        navigate(generateChartLayoutRouterPath({ timeframe, dateRange: value }))
      }
    },
    [navigate, customDateRangeDialogState, generateChartLayoutRouterPath, isPremium]
  )

  return {
    timeFrameGroups,
    dateRangeGroups,
    customDateRangeDialogState,
    handleDateRangeSelect,
    generateChartLayoutRouterPath,
  }
}
